import React, { useCallback, useState, useRef } from 'react';
import { useWidgetContext } from 'widgets/WidgetContext';
import useWidgetStream from 'widgets/streaming/useWidgetStream';
import classNames from 'classnames';
import { useEstimatedTradeCostComparisonWidgetConfigSlice } from 'widgets/estimatedTradeCost/widget-comparison/store/configslice';
import styleConstants from 'styles/variables.module.scss';
import {
    useEstimatedTradeCostComparisonData,
    useReRunEstimatedTradeCostComparisonRequests,
} from '../hooks';
import EstimatedTradeCostComparisonToolbar from './toolbar/EstimatedTradeCostComparisonToolbar';
import { useWidgetHasValidSelection } from 'widgets/hooks';
import InfoBox from 'utils/components/InfoBox/InfoBox';
import WidgetSettingsButton, {
    OnSaveHandler,
} from 'widgets/WidgetToolbar/WidgetSettingsButton';
import { EstimatedTradeCostComparisonWidgetSettings } from './settings/EstimatedTradeCostComparisonWidgetSettings';
import { EstimatedTradeCostComparisonWidgetContent } from './EstimatedTradeCostComparisonWidgetContent';
import { doNothing } from 'utils/general';

import './EstimatedTradeCostComparisonWidget.scss';
import { GridApi } from '@instinet-analytics/semantic-ui-components';
import { useEditableQuantity } from 'widgets/estimatedTradeCost/widget/hooks';
import {
    EstimatedTradeCostComparisonData,
    extractMessages,
} from 'widgets/estimatedTradeCost/widget-comparison/estimatedTradeCostComparisonData';

const padding = 2 * parseInt(styleConstants.spaceAround);
const toolbarHeight = parseInt(styleConstants.panelHeaderHeight);
const borderWidth = 2 * parseInt(styleConstants.layoutBorderWidth);

export function EstimatedTradeCostComparisonWidget() {
    const data = useEstimatedTradeCostComparisonData();
    const { height, widgetKey } = useWidgetContext();
    const { config } = useEstimatedTradeCostComparisonWidgetConfigSlice();

    const { hideToolbar: configHideToolbar } = config;
    const reRun = useReRunEstimatedTradeCostComparisonRequests();
    const configured = config.optimizations.length > 0;
    const [gridApi, setGridApi] = useState<GridApi | undefined>();

    const { editedQty, onQtyChanged, isQuantityDirty, dispatchEditedQuantity } =
        useEditableQuantity(data?.firstRequest);

    useWidgetStream();

    const hideToolbar = configHideToolbar; // !editedQty &&

    const toolbarConsumedHeight = hideToolbar ? 0 : toolbarHeight;
    const bodyHeight = hideToolbar
        ? height - borderWidth - padding
        : height - toolbarConsumedHeight - borderWidth - padding;

    const onRerunQueries = useCallback(() => {
        dispatchEditedQuantity();
        reRun();
    }, [dispatchEditedQuantity, reRun]);

    const cssClasses = classNames(
        'EstimatedTradeCostComparisonWidget',
        (editedQty || data.specifiedQty) && 'edited-qty',
        isQuantityDirty && 'dirty-qty'
    );

    const messages = extractMessages(data);

    return (
        <div className={cssClasses} data-widget-id={widgetKey}>
            <EstimatedTradeCostComparisonToolbar
                gridApi={gridApi}
                hideSelectionWarning={!configured}
                mustShow={!!editedQty}
                onRerunQuery={onRerunQueries}
                showRefresh={
                    data.allCompleted || data.haveFailure || !!editedQty
                }
                messages={messages}
            />

            <WidgetBody
                configured={configured}
                data={data}
                height={bodyHeight}
                onGridApiSet={setGridApi}
                editedQty={editedQty}
                onQtyChanged={onQtyChanged}
                isQuantityDirty={isQuantityDirty}
                onClickRefresh={onRerunQueries}
            />
        </div>
    );
}

export function WidgetBody({
    configured,
    data,
    editedQty,
    height,
    onGridApiSet,
    onQtyChanged,
    isQuantityDirty,
    onClickRefresh,
}: {
    configured: boolean;
    data: EstimatedTradeCostComparisonData | undefined;
    height: number;
    editedQty: number | undefined;
    isQuantityDirty: boolean;
    onQtyChanged: (qty: number) => void;
    onGridApiSet: (gridApi: GridApi) => void;
    onClickRefresh: () => void;
}) {
    const hasValidSelection = useWidgetHasValidSelection();

    if (!configured) {
        return <NotConfiguredWarning />;
    }

    if (!hasValidSelection) {
        return <></>;
    }

    return (
        <EstimatedTradeCostComparisonWidgetContent
            data={data}
            height={height}
            editedQty={editedQty}
            isQuantityDirty={isQuantityDirty}
            onGridApiSet={onGridApiSet}
            onQtyChanged={onQtyChanged}
            onClickRefresh={onClickRefresh}
        />
    );
}

function NotConfiguredWarning() {
    const onSaveHandler = useRef<OnSaveHandler>(doNothing);

    const setOnSaveHander = (handler: OnSaveHandler) => {
        onSaveHandler.current = handler;
    };

    const onSave = () => {
        return onSaveHandler.current();
    };

    return (
        <InfoBox
            className="configure-box info-box"
            info={
                <>
                    <span>
                        Configure at least one optimization in&nbsp;
                        <WidgetSettingsButton
                            header={'Pre-Trade Cost Comparison Settings'}
                            triggerText="Widget Settings"
                            allowCancel={true}
                            saveText={'OK'}
                            onSave={onSave}
                            body={
                                <EstimatedTradeCostComparisonWidgetSettings
                                    handleOnSave={setOnSaveHander}
                                />
                            }
                            className={
                                'EstimatedTradeCostComparisonWidgetSettingsModal'
                            }
                        />
                        .
                    </span>
                </>
            }
        />
    );
}
